/**
 * MerchantProxyListingsManager — read-only listings viewer that fetches data
 * from the Merchant Platform via the backend server-to-server proxy.
 *
 * Backend: /api/admin/merchant-proxy/items?store_type={storeType}
 *
 * Supports per-row suspend / resume / delete actions that forward to the
 * upstream Merchant Platform via PUT/DELETE on the proxy.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Search, RefreshCw, Pause, Play, Trash2, X, Inbox, ExternalLink,
  AlertTriangle, Eye, Image as ImageIcon, Pencil, Save,
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  active: { ar: 'فعّال', en: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  paused: { ar: 'غير فعال', en: 'Inactive', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  suspended: { ar: 'غير فعال', en: 'Inactive', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  store_stopped: { ar: 'غير فعال', en: 'Inactive', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  archived: { ar: 'مؤرشف', en: 'Archived', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
  pending: { ar: 'بالانتظار', en: 'Pending', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  rejected: { ar: 'مرفوض', en: 'Rejected', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const ACCENT = {
  emerald: 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10',
  amber:   'border-amber-500/30 text-amber-300 bg-amber-500/10',
  blue:    'border-blue-500/30 text-blue-300 bg-blue-500/10',
  purple:  'border-purple-500/30 text-purple-300 bg-purple-500/10',
  orange:  'border-orange-500/30 text-orange-300 bg-orange-500/10',
};

function StatusPill({ status, language }) {
  const def = STATUS_PILL[status] || STATUS_PILL.active;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${def.cls}`}>
      {language === 'ar' ? def.ar : def.en}
    </span>
  );
}

function pickTitle(item, language) {
  return (
    item.title_ar || item.name_ar || item.title || item.name ||
    item.label_ar || item.label_en || item.id || '—'
  );
}

function pickThumb(item) {
  return (
    item.image || item.thumbnail || item.image_url ||
    (Array.isArray(item.images) && item.images[0]) ||
    (Array.isArray(item.photos) && item.photos[0]) ||
    null
  );
}

function pickPrice(item) {
  if (item.price != null) return item.price;
  if (item.amount != null) return item.amount;
  if (item.value != null) return item.value;
  return null;
}

// Editable fields per store_type — must mirror backend `_EDITABLE_FIELDS`.
const EDITABLE_FIELDS = {
  cars:        [['title', 'text'], ['title_ar', 'text'], ['description', 'textarea'], ['price', 'number'], ['original_price', 'number'], ['currency', 'text'], ['brand', 'text'], ['model', 'text'], ['year', 'number'], ['mileage', 'number'], ['color', 'text'], ['location', 'text'], ['image', 'text']],
  car_plates:  [['title_ar', 'text'], ['price', 'number'], ['original_price', 'number'], ['currency', 'text'], ['emirate', 'text'], ['code', 'text'], ['number', 'text'], ['plate_display', 'text'], ['image', 'text']],
  numbers:     [['title', 'text'], ['title_ar', 'text'], ['price', 'number'], ['original_price', 'number'], ['currency', 'text'], ['number', 'text'], ['operator', 'text'], ['category', 'text'], ['image', 'text']],
  real_estate: [['title', 'text'], ['title_ar', 'text'], ['description', 'textarea'], ['price', 'number'], ['original_price', 'number'], ['currency', 'text'], ['type', 'text'], ['area', 'text'], ['location', 'text'], ['bedrooms', 'number'], ['bathrooms', 'number'], ['size', 'number'], ['image', 'text']],
  ecommerce:   [['name', 'text'], ['name_en', 'text'], ['title', 'text'], ['title_ar', 'text'], ['description', 'textarea'], ['price', 'number'], ['original_price', 'number'], ['currency', 'text'], ['category', 'text'], ['quantity', 'number'], ['image', 'text']],
};

const FIELD_LABELS = {
  title: { ar: 'العنوان (إنجليزي)', en: 'Title (EN)' },
  title_ar: { ar: 'العنوان (عربي)', en: 'Title (AR)' },
  description: { ar: 'الوصف', en: 'Description' },
  price: { ar: 'السعر', en: 'Price' },
  original_price: { ar: 'السعر الأصلي', en: 'Original Price' },
  currency: { ar: 'العملة', en: 'Currency' },
  brand: { ar: 'الماركة', en: 'Brand' },
  model: { ar: 'الموديل', en: 'Model' },
  year: { ar: 'السنة', en: 'Year' },
  mileage: { ar: 'الكيلومترات', en: 'Mileage' },
  color: { ar: 'اللون', en: 'Color' },
  location: { ar: 'الموقع', en: 'Location' },
  image: { ar: 'رابط الصورة', en: 'Image URL' },
  emirate: { ar: 'الإمارة', en: 'Emirate' },
  code: { ar: 'الرمز', en: 'Code' },
  number: { ar: 'الرقم', en: 'Number' },
  plate_display: { ar: 'عرض اللوحة', en: 'Plate Display' },
  operator: { ar: 'المشغّل', en: 'Operator' },
  category: { ar: 'الفئة', en: 'Category' },
  type: { ar: 'النوع', en: 'Type' },
  area: { ar: 'المنطقة', en: 'Area' },
  bedrooms: { ar: 'غرف النوم', en: 'Bedrooms' },
  bathrooms: { ar: 'الحمّامات', en: 'Bathrooms' },
  size: { ar: 'المساحة', en: 'Size (sqft)' },
  name: { ar: 'الاسم', en: 'Name' },
  name_en: { ar: 'الاسم (إنجليزي)', en: 'Name (EN)' },
  quantity: { ar: 'الكمية', en: 'Quantity' },
};

export default function MerchantProxyListingsManager({
  storeType,
  titleAr,
  titleEn,
  iconColor = 'emerald',
  testidPrefix,
  onBack,
}) {
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(null);   // item being edited
  const [editForm, setEditForm] = useState({});   // form values
  const [savingEdit, setSavingEdit] = useState(false);
  const [busy, setBusy] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ store_type: storeType });
      if (search.trim()) params.append('q', search.trim());
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(
        `${API_URL}/api/admin/merchant-proxy/items?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setItems(res.data?.data || []);
      setMeta(res.data || {});
    } catch (err) {
      setItems([]);
      setMeta({});
      toast.error(
        err.response?.data?.detail?.message ||
        err.response?.data?.detail ||
        (language === 'ar' ? 'فشل الاتصال بمنصة التجار' : 'Failed to reach Merchant Platform')
      );
    } finally {
      setLoading(false);
    }
  }, [storeType, search, statusFilter, language]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateStatus = async (item, newStatus, reason = null) => {
    setBusy(item.id);
    try {
      await axios.put(
        `${API_URL}/api/admin/merchant-proxy/items/${storeType}/${item.id}/status`,
        { status: newStatus, ...(reason ? { reason } : {}) },
        { headers: getAuthHeaders() }
      );
      toast.success(
        language === 'ar' ? 'تم تحديث الحالة' : 'Status updated'
      );
      fetchItems();
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.message ||
        err.response?.data?.detail ||
        (language === 'ar' ? 'فشل التحديث' : 'Update failed')
      );
    } finally {
      setBusy(null);
    }
  };

  const deleteItem = async (item) => {
    setBusy(item.id);
    try {
      await axios.delete(
        `${API_URL}/api/admin/merchant-proxy/items/${storeType}/${item.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      setConfirm(null);
      fetchItems();
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.message ||
        err.response?.data?.detail ||
        (language === 'ar' ? 'فشل الحذف' : 'Delete failed')
      );
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (item) => {
    const raw = item.raw || item;
    const fields = EDITABLE_FIELDS[storeType] || [];
    const initial = {};
    fields.forEach(([key]) => { initial[key] = raw[key] != null ? raw[key] : ''; });
    setEditForm(initial);
    setEditing(item);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      // Build diff: send only fields whose value actually changed
      const raw = editing.raw || editing;
      const changed = {};
      Object.entries(editForm).forEach(([k, v]) => {
        const before = raw[k];
        if (v === '' && before == null) return;            // both empty → skip
        if (String(before ?? '') === String(v)) return;     // unchanged → skip
        // Type coercion for number fields
        const fieldType = (EDITABLE_FIELDS[storeType] || []).find(([k2]) => k2 === k)?.[1];
        if (fieldType === 'number' && v !== '' && v !== null) {
          const n = Number(v);
          if (!Number.isNaN(n)) changed[k] = n;
        } else {
          changed[k] = v;
        }
      });
      if (Object.keys(changed).length === 0) {
        toast.info(language === 'ar' ? 'لا توجد تغييرات للحفظ' : 'No changes to save');
        setSavingEdit(false);
        return;
      }
      const res = await axios.patch(
        `${API_URL}/api/admin/merchant-proxy/items/${storeType}/${editing.id}`,
        changed,
        { headers: getAuthHeaders() }
      );
      const modified = res.data?.modified || 0;
      toast.success(
        language === 'ar'
          ? `✅ تم حفظ ${Object.keys(changed).length} حقل (${modified ? 'محدّث' : 'بدون تغيير'})`
          : `✅ Saved ${Object.keys(changed).length} field(s) (${modified ? 'updated' : 'no change'})`
      );
      setEditing(null);
      setEditForm({});
      fetchItems();
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.message ||
        err.response?.data?.detail ||
        (language === 'ar' ? 'فشل الحفظ' : 'Save failed')
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const sourceBadge = meta.source === 'merchant_db_direct'
    ? { ar: 'DB مباشر', en: 'Live DB', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' }
    : meta.source === 'admin'
    ? { ar: 'API: إدارة', en: 'API: Admin', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' }
    : meta.source === 'public'
    ? { ar: 'API: عام', en: 'API: Public', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' }
    : meta.source === 'unreachable'
    ? { ar: 'تعذّر الاتصال', en: 'Unreachable', cls: 'bg-red-500/20 text-red-300 border-red-500/40' }
    : null;

  const accentCls = ACCENT[iconColor] || ACCENT.emerald;

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-proxy-manager`}>
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">
            {language === 'ar' ? titleAr : titleEn}
          </h2>
          <p className="text-xs text-[#808080] mt-1">
            {language === 'ar'
              ? `البيانات مباشرة من قاعدة بيانات منصة التجار${meta.collection ? ` (${meta.collection})` : ''}`
              : `Live data from the Merchant Platform DB${meta.collection ? ` (${meta.collection})` : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-3 py-1.5 text-xs rounded-lg border font-bold ${accentCls}`}>
            {items.length} {language === 'ar' ? 'عنصر' : 'items'}
          </span>
          {sourceBadge && (
            <span className={`px-3 py-1.5 text-[10px] rounded-lg border font-mono ${sourceBadge.cls}`}>
              {language === 'ar' ? sourceBadge.ar : sourceBadge.en}
            </span>
          )}
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-1.5 text-sm rounded-xl border border-[#2a2a4e] text-white hover:bg-[#2a2a4e] transition-colors"
              data-testid={`${testidPrefix}-back-btn`}
            >
              {language === 'ar' ? 'رجوع' : 'Back'}
            </button>
          )}
        </div>
      </div>

      {/* Unreachable banner */}
      {meta.source === 'unreachable' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-300 mb-1">
              {language === 'ar' ? 'تعذّر الوصول لمنصة التجار' : 'Merchant Platform unreachable'}
            </p>
            <p className="text-xs text-[#A1A1AA]">
              {language === 'ar' ? meta.message_ar : meta.message_en}
            </p>
            {meta.upstream_url && (
              <p className="text-[10px] text-[#71717A] mt-2 font-mono break-all">
                {meta.upstream_url}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Search + filter + refresh */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
            className="w-full ps-3 pe-9 py-2.5 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50"
            data-testid={`${testidPrefix}-search`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:outline-none"
          data-testid={`${testidPrefix}-status-filter`}
        >
          <option value="">{language === 'ar' ? 'كل الحالات' : 'All Statuses'}</option>
          <option value="active">{language === 'ar' ? 'فعّال' : 'Active'}</option>
          <option value="paused">{language === 'ar' ? 'موقوف' : 'Paused'}</option>
          <option value="archived">{language === 'ar' ? 'مؤرشف' : 'Archived'}</option>
        </select>
        <button
          onClick={fetchItems}
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-[#A1A1AA] hover:text-white transition-colors"
          data-testid={`${testidPrefix}-refresh`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* List */}
      <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-7 w-7 animate-spin text-emerald-400 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#808080]" data-testid={`${testidPrefix}-empty`}>
            <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>{language === 'ar' ? 'لا توجد معروضات' : 'No listings'}</p>
            <p className="text-[10px] mt-2 text-[#71717A]">
              {language === 'ar'
                ? 'سيظهرون هنا فور إضافة معروضات على منصة التجار'
                : 'They will appear once merchants add listings on the platform'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
            {items.map((item, idx) => {
              const thumb = pickThumb(item);
              const price = pickPrice(item);
              const status = item.status || 'active';
              const storeStopped = !!item.store_stopped;
              const effectiveStatus = storeStopped ? 'store_stopped' : status;
              const reason = storeStopped
                ? (language === 'ar' ? 'إيقاف المتجر' : 'Store suspended')
                : (status !== 'active' && status !== 'archived')
                  ? (item.moderation_reason || (language === 'ar' ? 'إيقاف من الإدارة' : 'Paused by admin'))
                  : null;
              const isBusy = busy === item.id;
              return (
                <motion.div
                  key={item.id || `row-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                  className="bg-[#0a0a0a] rounded-xl border border-white/10 hover:border-white/20 transition-colors overflow-hidden"
                  data-testid={`${testidPrefix}-row-${item.id}`}
                >
                  {/* Thumb */}
                  <div className="relative h-32 bg-gradient-to-br from-white/5 to-white/[0.02] flex items-center justify-center">
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="h-10 w-10 text-white/10" />
                    )}
                    <div className="absolute top-2 start-2">
                      <StatusPill status={effectiveStatus} language={language} />
                    </div>
                  </div>
                  {/* Body */}
                  <div className="p-3">
                    <p className="text-sm font-bold text-white truncate mb-1">
                      {pickTitle(item, language)}
                    </p>
                    {price != null && (
                      <p className="text-xs text-emerald-300 mb-2">
                        {price} {item.currency || 'AED'}
                      </p>
                    )}
                    {item.store_name && (
                      <p className="text-[10px] text-[#71717A] truncate">
                        {language === 'ar' ? 'المتجر: ' : 'Store: '}
                        <span className="text-white">{item.store_name}</span>
                      </p>
                    )}
                    {item.owner_name && (
                      <p className="text-[10px] text-[#71717A] truncate">
                        {language === 'ar' ? 'المالك: ' : 'Owner: '}
                        <span className="text-white">{item.owner_name}</span>
                      </p>
                    )}
                    {reason && (
                      <p className="text-[10px] mt-1.5 text-red-300/90 flex items-center gap-1" data-testid={`${testidPrefix}-reason-${item.id}`}>
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {language === 'ar' ? `السبب: ${reason}` : `Reason: ${reason}`}
                      </p>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex border-t border-white/10">
                    <button
                      onClick={() => setDetails(item)}
                      className="flex-1 py-2 text-xs text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors flex items-center justify-center gap-1"
                      data-testid={`${testidPrefix}-view-${item.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      {language === 'ar' ? 'عرض' : 'View'}
                    </button>
                    <button
                      onClick={() => openEdit(item)}
                      disabled={isBusy}
                      className="flex-1 py-2 text-xs text-blue-300 hover:bg-blue-500/10 transition-colors flex items-center justify-center gap-1 border-s border-white/10 disabled:opacity-50"
                      data-testid={`${testidPrefix}-edit-${item.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {language === 'ar' ? 'تعديل' : 'Edit'}
                    </button>
                    {storeStopped ? (
                      <button
                        disabled
                        title={language === 'ar' ? 'لا يمكن التفعيل — المتجر موقوف. أعد تفعيل المتجر أولاً.' : 'Cannot activate — store is suspended. Re-activate the store first.'}
                        className="flex-1 py-2 text-xs text-white/30 cursor-not-allowed flex items-center justify-center gap-1 border-s border-white/10"
                        data-testid={`${testidPrefix}-resume-locked-${item.id}`}
                      >
                        <Play className="h-3.5 w-3.5" />
                        {language === 'ar' ? 'تفعيل' : 'Resume'}
                      </button>
                    ) : status === 'active' ? (
                      <button
                        onClick={() => updateStatus(item, 'paused')}
                        disabled={isBusy}
                        className="flex-1 py-2 text-xs text-amber-300 hover:bg-amber-500/10 transition-colors flex items-center justify-center gap-1 border-s border-white/10 disabled:opacity-50"
                        data-testid={`${testidPrefix}-pause-${item.id}`}
                      >
                        <Pause className="h-3.5 w-3.5" />
                        {language === 'ar' ? 'إيقاف' : 'Pause'}
                      </button>
                    ) : (
                      <button
                        onClick={() => updateStatus(item, 'active')}
                        disabled={isBusy}
                        className="flex-1 py-2 text-xs text-emerald-300 hover:bg-emerald-500/10 transition-colors flex items-center justify-center gap-1 border-s border-white/10 disabled:opacity-50"
                        data-testid={`${testidPrefix}-resume-${item.id}`}
                      >
                        <Play className="h-3.5 w-3.5" />
                        {language === 'ar' ? 'تفعيل' : 'Resume'}
                      </button>
                    )}
                    <button
                      onClick={() => setConfirm(item)}
                      disabled={isBusy}
                      className="flex-1 py-2 text-xs text-red-300 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-1 border-s border-white/10 disabled:opacity-50"
                      data-testid={`${testidPrefix}-delete-${item.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {language === 'ar' ? 'حذف' : 'Delete'}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <AnimatePresence>
        {confirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-md w-full"
              data-testid={`${testidPrefix}-delete-modal`}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">
                {language === 'ar' ? 'حذف نهائي' : 'Permanent Delete'}
              </h3>
              <p className="text-[#A1A1AA] text-center text-sm mb-5">
                {language === 'ar'
                  ? 'سيتم الحذف من منصة التجار. هذا الإجراء غير قابل للتراجع.'
                  : 'This will delete the listing from the Merchant Platform. This action cannot be undone.'}
              </p>
              <p className="text-xs text-white text-center mb-5 font-medium">
                {pickTitle(confirm, language)}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirm(null)}
                  className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={() => deleteItem(confirm)}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600"
                  data-testid={`${testidPrefix}-delete-confirm`}
                >
                  {language === 'ar' ? 'حذف' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !savingEdit && setEditing(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-blue-500/30 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              data-testid={`${testidPrefix}-edit-modal`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Pencil className="h-5 w-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">
                    {language === 'ar' ? 'تعديل المعروض' : 'Edit Listing'}
                  </h3>
                </div>
                <button
                  onClick={() => !savingEdit && setEditing(null)}
                  className="p-1 text-[#71717A] hover:text-white"
                  disabled={savingEdit}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-[10px] text-[#71717A] mb-4 font-mono break-all">
                ID: {editing.id} · {storeType}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(EDITABLE_FIELDS[storeType] || []).map(([key, type]) => {
                  const label = FIELD_LABELS[key]?.[language] || key;
                  const value = editForm[key] ?? '';
                  const isTextarea = type === 'textarea';
                  return (
                    <div key={key} className={isTextarea ? 'md:col-span-2' : ''}>
                      <label className="block text-xs text-[#A1A1AA] mb-1.5">
                        {label}
                        <span className="text-[#71717A] font-mono text-[10px] ms-2">({key})</span>
                      </label>
                      {isTextarea ? (
                        <textarea
                          value={value}
                          onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50 resize-none"
                          data-testid={`${testidPrefix}-edit-field-${key}`}
                        />
                      ) : (
                        <input
                          type={type === 'number' ? 'number' : 'text'}
                          value={value}
                          onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                          className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50"
                          data-testid={`${testidPrefix}-edit-field-${key}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
                <button
                  onClick={() => !savingEdit && setEditing(null)}
                  disabled={savingEdit}
                  className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 disabled:opacity-50"
                  data-testid={`${testidPrefix}-edit-cancel`}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid={`${testidPrefix}-edit-save`}
                >
                  {savingEdit ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {language === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Details modal */}
      <AnimatePresence>
        {details && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDetails(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              data-testid={`${testidPrefix}-details-modal`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'تفاصيل المعروض' : 'Listing Details'}
                </h3>
                <button onClick={() => setDetails(null)} className="p-1 text-[#71717A] hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                {Object.entries(details).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5">
                    <span className="text-xs text-[#71717A] font-mono shrink-0">{k}</span>
                    <span className="text-white text-xs text-end break-all max-w-[60%]">
                      {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </div>
                ))}
              </div>
              {meta.upstream_url && (
                <div className="mt-4 pt-3 border-t border-white/10 text-[10px] text-[#71717A] font-mono break-all flex items-center gap-2">
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span>{meta.upstream_url}</span>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
